/**
 * The Access page's work queues (19 Oct, Rusty's target New user flow), for
 * admins and IT, beside Requests and Completed:
 *
 * - **Global IT** (`?view=global-it`): new hires waiting for the account, the
 *   laptop and the core AD, soonest start first, with the gate in a word: in
 *   ITSM yes / check (IT confirms which person) / no. A case started by
 *   someone not on the HR validation list is flagged.
 * - **Backoffice** (`?view=backoffice`): "Roles to select" (New users past
 *   the gate whose business system roles are still to pick, with the
 *   reference user and, once 365FO can be read, their actual roles) and
 *   "Ready to assign" (approved: assign; rejected: close, the approver's
 *   comment highlighted; filed with nothing pending: work).
 *
 * The server answers 403 to anyone else; the tabs show on `access_audit`.
 */

import { Alert, Badge, Button, Card, Collapsible, Typography } from "@norican/react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  ResultStateNotice,
  useDateFormatter,
} from "@/components/common";
import { startDateValue } from "@/features/starters";
import { useT } from "@/i18n";
import type {
  AccessAssignRow,
  AccessGlobalItRow,
  AccessQueueItem,
  AccessRolesRow,
} from "@/lib/api";

import { useAccessQueues, useAccessReferenceRoles, useReferenceAccess } from "../api";
import { accessItemPath, itemLabelKey, statusTone } from "../lib/access";
import { ReferenceEntries, ReferenceSelectionView, SourceLines } from "./reference-access";

type QueueView = "global-it" | "backoffice";
/** The fields every queue row shares (the server's `AccessQueueCase`). */
type QueueCase = AccessGlobalItRow | AccessRolesRow | AccessAssignRow;

/** "Assign" is a button (19 Oct, UI review finding 9): its words per next step. */
const NEXT_BUTTON = {
  grant: "access.queues.assign.button.grant",
  close: "access.queues.assign.button.close",
  work: "access.queues.assign.button.work",
} as const;

export function AccessQueuesPanel({ view }: { view: QueueView }) {
  const { t } = useT("tickets");
  const query = useAccessQueues(true);

  if (query.isPending) {
    return <LoadingRows rows={3} />;
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  const data = query.data;
  return (
    <div className="flex flex-col gap-5">
      <ResultStateNotice result={data.result} />
      {view === "global-it" ? (
        <Queue
          title={t("access.queues.globalIt.title")}
          hint={
            data.auto_gate === true
              ? t("access.queues.globalIt.hintAuto")
              : t("access.queues.globalIt.hint")
          }
          count={data.global_it?.length ?? 0}
          empty={t("access.queues.globalIt.empty")}
        >
          {(data.global_it ?? []).map((row) => (
            <GlobalItCard key={row.case_id} row={row} autoGate={data.auto_gate === true} />
          ))}
        </Queue>
      ) : (
        <>
          <Queue
            title={t("access.queues.roles.title")}
            hint={t("access.queues.roles.hint")}
            count={data.roles_to_select?.length ?? 0}
            empty={t("access.queues.roles.empty")}
            notice={
              // Once for the queue, not on every card (finding 9). 21 Oct:
              // with the reference access on, each card shows what can be read.
              data.reference_import !== true &&
              data.reference_roles_available !== true &&
              (data.roles_to_select ?? []).some((row) => row.reference_user_id) ? (
                <Alert tone="info">{t("access.queues.roles.notAvailable")}</Alert>
              ) : null
            }
          >
            {(data.roles_to_select ?? []).map((row) => (
              <RolesCard
                key={row.case_id}
                row={row}
                rolesReadable={data.reference_roles_available === true}
                referenceImport={data.reference_import === true}
              />
            ))}
          </Queue>
          <Queue
            title={t("access.queues.assign.title")}
            hint={t("access.queues.assign.hint")}
            count={data.ready_to_assign?.length ?? 0}
            empty={t("access.queues.assign.empty")}
          >
            {(data.ready_to_assign ?? []).map((row) => (
              <AssignCard key={row.case_id} row={row} />
            ))}
          </Queue>
        </>
      )}
    </div>
  );
}

function Queue({
  title,
  hint,
  count,
  empty,
  notice = null,
  children,
}: {
  title: string;
  hint: string;
  count: number;
  empty: string;
  notice?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <Typography as="h2" variant="heading-6">
          {title}
        </Typography>
        <Badge tone="neutral" variant="subtle" size="xs">
          {count}
        </Badge>
      </span>
      <Typography as="p" variant="body-sm" tone="muted">
        {hint}
      </Typography>
      {count === 0 ? null : notice}
      {count === 0 ? (
        <EmptyStateBlock title={empty} />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">{children}</ul>
      )}
    </section>
  );
}

/** Who, the type and request, the flag; then start date, site, manager, requester. */
function CaseHead({ row, extra }: { row: QueueCase; extra?: ReactNode }) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const start = startDateValue(row.start_date);
  const facts = [
    start !== null ? t("access.startDate", { date: formatDate(start) }) : null,
    row.site ?? null,
    row.manager ? t("access.manager", { name: row.manager }) : null,
    row.requested_by ? t("access.requestedBy", { name: row.requested_by }) : null,
  ].filter((part): part is string => part !== null && part !== "");
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <Link to={row.url_path} className="min-w-0 rounded-md break-words">
          <Typography as="span" variant="heading-6">
            {row.person_name}
          </Typography>
        </Link>
        <Typography as="span" variant="body-sm" className="text-fg-muted">
          {`${t(`access.type.${row.type}`)} · ${t("access.caseNumber", { number: row.case_number })}`}
        </Typography>
        {extra}
      </span>
      {facts.length > 0 ? (
        <Typography as="span" variant="body-sm" className="break-words">
          {facts.join(" · ")}
        </Typography>
      ) : null}
    </div>
  );
}

function GlobalItCard({ row, autoGate }: { row: AccessGlobalItRow; autoGate: boolean }) {
  const { t } = useT("tickets");
  const inItsm = row.in_itsm ?? "no";
  // 21 Oct (decision A): Global IT does nothing here; the HelpDesk matches
  // the person in ITSM itself, so "check" means it is still looking.
  const inItsmLabel =
    autoGate && inItsm !== "yes"
      ? t("access.queues.inItsm.matching")
      : t(`access.queues.inItsm.${inItsm}`);
  return (
    <li>
      <Card variant="outline" padding="sm" className="flex flex-col gap-2">
        <CaseHead
          row={row}
          extra={
            <Badge tone={statusTone("inItsm", inItsm)} variant="subtle" size="xs">
              {inItsmLabel}
            </Badge>
          }
        />
        <Typography as="p" variant="body-sm" className="break-words">
          {[
            row.owner ? t("access.queues.globalIt.owner", { name: row.owner }) : null,
            row.status ? t("access.queues.globalIt.status", { status: row.status }) : null,
            (row.held_items ?? []).length > 0
              ? t("access.queues.globalIt.held", { list: (row.held_items ?? []).join(", ") })
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
        {/* Finding 9: the next step as a button. Confirming the person is on
            the case page's account section (the starter's gate). */}
        {inItsm === "check" && !autoGate ? (
          <Button size="sm" className="self-start" render={<Link to={`${row.url_path}#account`} />}>
            {t("access.queues.globalIt.confirm")}
          </Button>
        ) : null}
      </Card>
    </li>
  );
}

function RolesCard({
  row,
  rolesReadable,
  referenceImport,
}: {
  row: AccessRolesRow;
  rolesReadable: boolean;
  referenceImport: boolean;
}) {
  const { t } = useT("tickets");
  return (
    <li>
      <Card variant="outline" padding="sm" className="flex flex-col gap-2">
        <CaseHead row={row} />
        <Typography as="p" variant="body-sm" className="break-words">
          {row.reference_user
            ? t("access.queues.roles.reference", { name: row.reference_user })
            : t("access.queues.roles.noReference")}
        </Typography>
        {referenceImport && row.reference_user_id ? (
          <ReferenceChosen row={row} />
        ) : row.reference_user_id && rolesReadable ? (
          <ReferenceRoles employeeId={row.reference_user_id} />
        ) : null}
        <span className="flex flex-wrap gap-2">
          {(row.items ?? []).map((item) => (
            <Button
              key={item.n}
              size="sm"
              className="max-sm:flex-1"
              render={<Link to={accessItemPath(row.case_id, item.n)} />}
            >
              {t("access.queues.roles.select", { item: item.label })}
            </Button>
          ))}
        </span>
      </Card>
    </li>
  );
}

/**
 * 21 Oct: what the requester kept, removed and added from the reference user
 * (the note on the New user request), and on request what that user has, per
 * system with its sources. Backoffice finishes the selection on the step's
 * form ("Select roles"), which shows the same view with ticks.
 */
function ReferenceChosen({ row }: { row: AccessRolesRow }) {
  const { t } = useT("tickets");
  const [open, setOpen] = useState(false);
  const name = row.reference_user ?? "";
  const query = useReferenceAccess(row.reference_user_id ?? null, open);
  const systems = (query.data?.systems ?? []).filter(
    (system) => (system.items ?? []).length > 0 || system.item_key !== null,
  );
  return (
    <div className="flex flex-col gap-2">
      <Typography as="p" variant="body-sm" className="font-medium">
        {t("access.reference.selection.title", { name })}
      </Typography>
      <ReferenceSelectionView
        lines={row.reference_selection ?? []}
        labelOf={(item) => t(itemLabelKey(item) as never)}
      />
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger render={<Button variant="ghost" tone="neutral" size="sm" />}>
          {open
            ? t("access.reference.selection.hide", { name })
            : t("access.reference.selection.show", { name })}
        </Collapsible.Trigger>
        <Collapsible.Panel>
          {query.isPending ? (
            <LoadingRows rows={2} />
          ) : query.isError ? (
            <Typography as="p" variant="caption" tone="muted">
              {t("access.reference.error", { name })}
            </Typography>
          ) : (
            <ul className="m-0 mt-2 grid list-none gap-3 p-0 sm:grid-cols-2">
              {systems.map((system) => (
                <li key={system.key} className="flex min-w-0 flex-col gap-1">
                  <Typography as="span" variant="label">
                    {t(`access.reference.system.${system.key}` as never)}
                  </Typography>
                  {(system.items ?? []).length > 0 ? (
                    <ReferenceEntries items={system.items ?? []} />
                  ) : null}
                  <SourceLines sources={system.sources ?? []} />
                </li>
              ))}
            </ul>
          )}
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  );
}

/** The reference user's actual roles (the queue says once when they cannot be read). */
function ReferenceRoles({ employeeId }: { employeeId: string }) {
  const { t } = useT("tickets");
  const query = useAccessReferenceRoles(employeeId);
  if (query.data?.status === "not_available") {
    return null;
  }
  if (query.isPending) {
    return <LoadingRows rows={1} />;
  }
  if (query.isError || query.data.status === "error") {
    return (
      <Typography as="p" variant="caption" tone="muted">
        {t("access.queues.roles.unreadable")}
      </Typography>
    );
  }
  return (
    <ul className="m-0 flex list-none flex-col gap-1 p-0">
      {(query.data.systems ?? []).map((system) => (
        <li key={system.system}>
          <Typography as="span" variant="caption">
            {`${system.label}: ${(system.roles ?? [])
              .map((role) => (role.company ? `${role.label} (${role.company})` : role.label))
              .join(", ")}`}
          </Typography>
        </li>
      ))}
    </ul>
  );
}

function AssignCard({ row }: { row: AccessAssignRow }) {
  return (
    <li>
      <Card
        variant="outline"
        padding="sm"
        accent={(row.items ?? []).some((i) => i.next === "close") ? "left" : undefined}
        accentTone={(row.items ?? []).some((i) => i.next === "close") ? "danger" : undefined}
        className="flex flex-col gap-2"
      >
        <CaseHead row={row} />
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {(row.items ?? []).map((item) => (
            <AssignItem key={item.n} item={item} caseId={row.case_id} />
          ))}
        </ul>
      </Card>
    </li>
  );
}

function AssignItem({ item, caseId }: { item: AccessQueueItem; caseId: string }) {
  const { t } = useT("tickets");
  const next = item.next ?? "grant";
  const target = item.request?.url_path || accessItemPath(caseId, item.n);
  return (
    <li className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <Typography as="span" variant="body-sm" className="font-medium break-words">
          {item.label}
        </Typography>
        {item.team ? (
          <Typography as="span" variant="body-sm" className="text-fg-muted">
            {item.team}
          </Typography>
        ) : null}
      </span>
      {(item.approved ?? []).length > 0 ? (
        <Typography as="span" variant="body-sm" className="break-words">
          {t("access.queues.assign.approved", { list: (item.approved ?? []).join("; ") })}
        </Typography>
      ) : null}
      {(item.rejections ?? []).map((decision) => (
        <span key={`r-${decision.what}`} className="flex min-w-0 flex-col">
          <Typography
            as="span"
            variant="body-sm"
            className="text-danger-fg font-medium break-words"
          >
            {t("access.queues.assign.rejected", {
              what: decision.what,
              who: decision.decided_by ?? "—",
            })}
          </Typography>
          <Typography as="span" variant="body-sm" className="break-words">
            {decision.reason
              ? t("access.rejection.reason", { reason: decision.reason })
              : t("access.rejection.noReason")}
          </Typography>
        </span>
      ))}
      {(item.comments ?? [])
        .filter((decision) => (decision.reason ?? "").trim() !== "")
        .map((decision) => (
          <Typography
            key={`c-${decision.what}`}
            as="span"
            variant="body-sm"
            className="text-warning-fg break-words"
          >
            {t("access.queues.assign.comment", {
              who: decision.decided_by ?? "—",
              reason: decision.reason ?? "",
            })}
          </Typography>
        ))}
      <Button
        size="sm"
        variant={next === "work" ? "outline" : "solid"}
        tone={next === "close" ? "danger" : undefined}
        className="self-start"
        render={<Link to={target} />}
      >
        {t(NEXT_BUTTON[next], { item: item.label })}
      </Button>
    </li>
  );
}
